import { z } from "zod";

import { AdminUser } from "./workforce.schema";

import { ERROR_MSG } from "@/constants/ErrorMessage.constant";
import {
  abnValidator,
  alphanumericValidator,
  basicValidator,
  emailValidator,
  phoneNumberValidator,
} from "@/lib/validators";

export const UpdateCustomerSchema = z.object({
  name: basicValidator(true, "Name is required", 2),
  officialName: basicValidator(true, "Customer company name is required", 1),
  shortName: alphanumericValidator({
    allowHyphen: false,
    required: true,
    allowSpace: false,
    minLength: 1,
    maxLength: 50,
    message: "Short Name is required",
  }),

  abn: abnValidator(true),
  email: emailValidator(false, ERROR_MSG.INVALID_EMAIL).transform((value) => {
    if (!value) {
      return null;
    }

    return value;
  }),
  address: z
    .object({
      label: z.string().trim().min(2, { message: "Address is required." }),
    })
    .superRefine((data, ctx) => {
      if (!data?.label) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Address is required.",
        });
      }
    }),
  phoneNumber: phoneNumberValidator(false, ERROR_MSG.INVALID_PHONE),
  image: z
    .array(
      z
        .instanceof(File, {
          message: "Required",
        })
        .or(z.string())
        .optional(),
    )
    .optional(),
  adminUsers: z.array(AdminUser).optional(),
  removeAdmin: z
    .array(
      z.object({
        id: z.string().uuid(),
        userId: z.string().uuid(),
      }),
    )
    .optional(),
});

export type UpdateCustomerSchemaTypes = z.infer<typeof UpdateCustomerSchema>;

export const addCustomerSchema = UpdateCustomerSchema.and(
  z.object({
    accountUser: z.object({
      name: z.object({
        first: basicValidator(true, "Account user first name is required", 1),
        last: basicValidator(true, "Account user last name is required", 1),
      }),
      phoneNumber: phoneNumberValidator(false, ERROR_MSG.INVALID_PHONE),
      email: z
        .string({
          required_error: "Account user email is required.",
        })
        .email({ message: ERROR_MSG.INVALID_EMAIL }),
      permissions: z.object({
        emailNotification: z.boolean(),
        customerPortal: z.boolean(),
        projectFinance: z.boolean(),
      }),
      position: z.string(),
    }),
  }),
);

export type TAddCustomerSchema = z.infer<typeof addCustomerSchema>;

export const CustomerUserPermission = z.object({
  emailNotification: z.boolean(),
  customerPortal: z.boolean(),
  projectFinance: z.boolean(),
});
export type T_CustomerUserPermission = z.infer<typeof CustomerUserPermission>;

export const CustomerUser = z.object({
  customerId: basicValidator(true, ERROR_MSG.REQUIRED),
  defaultCustomerLabel: z.string().optional(),
  position: basicValidator(true, ERROR_MSG.REQUIRED),
  name: z.object({
    first: basicValidator(true, ERROR_MSG.REQUIRED, 1),
    last: basicValidator(true, ERROR_MSG.REQUIRED, 1),
  }),
  email: emailValidator(false, ERROR_MSG.INVALID_EMAIL).transform((value) => {
    if (!value) {
      return null;
    }

    return value;
  }),
  phoneNumber: phoneNumberValidator(false, ERROR_MSG.INVALID_PHONE).optional(),
  image: z
    .array(
      z
        .instanceof(File, {
          message: ERROR_MSG.REQUIRED,
        })
        .or(z.string())
        .optional(),
    )
    .optional(),
  permissions: CustomerUserPermission,
});

export type TcustomerUser = z.infer<typeof CustomerUser>;

export const prospect = z.object({
  name: basicValidator(true, ERROR_MSG.REQUIRED, 2),
  shortName: alphanumericValidator({
    allowHyphen: false,
    required: true,
    allowSpace: false,
    minLength: 2,
    maxLength: 4,
    message: "Short Name is required.",
  }),
  officialName: z
    .string({
      required_error: "Company Name is required.",
    })
    .optional(),
  email: emailValidator(false, ERROR_MSG.INVALID_EMAIL),
  phoneNumber: phoneNumberValidator(false, ERROR_MSG.INVALID_PHONE).optional(),
  abn: abnValidator(false).optional(),
  address: z.object({
    label: z.string().superRefine((data, ctx) => {
      if (data.length < 3) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Address is required.",
        });
      }
    }),
  }),
  image: z
    .array(
      z
        .instanceof(File, {
          message: "Required",
        })
        .or(z.string())
        .optional(),
    )
    .optional(),
  adminUsers: z.array(AdminUser).optional(),
  removeAdmin: z.array(z.string().uuid().optional()).optional(),
});

export type TProspectSchema = z.infer<typeof prospect>;

export const addProspectUser = prospect.extend({
  accountName: z.object({
    first: z
      .string({
        required_error: "First Name is required.",
      })
      .min(2, {
        message: "First Name is required.",
      }),
    // .max(50, {
    //   message: "First Name is too long.",
    // }),
    last: z
      .string({
        required_error: "Last Name is required.",
      })
      .min(2, {
        message: "Last Name is required.",
      }),
    // .max(50, {
    //   message: "Last Name is too long.",
    // }),
  }),
  accountEmail: z.string().min(2).email({ message: ERROR_MSG.INVALID_EMAIL }),
});

export type TAddProspectUser = z.infer<typeof addProspectUser>;

export const ProspectUser = z.object({
  prospectId: z.string().uuid({
    message: "Required",
  }),
  name: z.object({
    first: z
      .string({
        required_error: "First Name is required.",
      })
      .min(2, {
        message: "First Name is required.",
      }),
    // .max(50, {
    //   message: "First Name is too long.",
    // }),
    last: z
      .string({
        required_error: "Last Name is required.",
      })
      .min(2, {
        message: "Last Name is required.",
      }),
    // .max(50, {
    //   message: "Last Name is too long.",
    // }),
  }),
  position: z.string().min(2, {
    message: "Required.",
  }),
  // .max(50),
  email: z.string().email({ message: ERROR_MSG.INVALID_EMAIL }),
  phoneNumber: phoneNumberValidator(false, ERROR_MSG.INVALID_PHONE).optional(),
  image: z
    .array(
      z
        .instanceof(File, {
          message: "Required",
        })
        .or(z.string())
        .optional(),
    )
    .optional(),
  permissions: z.object({
    emailNotification: z.boolean(),
    customerPortal: z.boolean(),
    projectFinance: z.boolean(),
  }),
  defaultProspectLabel: z.string().optional(),
});

export type TProspectUserSchema = z.infer<typeof ProspectUser>;
